import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { campaignsApi, type Campaign } from '../api/campaigns';
import {
  Alert,
  Card,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
  RowActions,
  Section,
  StatusChip,
  Table,
} from '../components';
import { useI18n } from '../i18n';
import { useSession } from '../auth/session-context';

/**
 * Startseite eines Callers.
 *
 * Sie zeigt, was ein Caller braucht, um loszulegen: die für ihn freigegebenen
 * Kampagnen. Tagesstatistik und Inbound-Bereitschaft kommen mit den
 * entsprechenden Funktionen dazu (02_navigation_ux_tutorial_i18n.md) - hier
 * steht bewusst nichts, was es noch nicht gibt.
 */
export function HomePage(): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();

  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setError(null);
    try {
      setCampaigns((await campaignsApi.listMine()).campaigns);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
      setCampaigns([]);
    }
  }, [handleApiError, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  if (!principal || principal.kind !== 'tenant_user') {
    return <PageLayout />;
  }

  const rolle = principal.isOwner ? 'home.role.owner' : principal.isAdmin ? 'home.role.admin' : 'home.role.caller';

  return (
    <PageLayout>
      <PageHeader title={t('home.greeting') + ', ' + principal.displayName} description={t(rolle)} />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {campaigns === null ? <LoadingState /> : null}

      {campaigns !== null ? (
        <Card>
          <Section title={t('nav.campaigns')}>
            {campaigns.length === 0 ? (
              <EmptyState title={t('campaigns.empty')} description={t('campaign.callers.hint')} />
            ) : (
              <Table
                label={t('nav.campaigns')}
                headers={[t('campaigns.name'), t('campaigns.state.active'), '']}
              >
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>
                      <strong>{campaign.name}</strong>
                      {campaign.description ? <div className="field-hint">{campaign.description}</div> : null}
                    </td>
                    <td>
                      <StatusChip tone={campaign.isActive ? 'erfolg' : 'warnung'}>
                        {campaign.isActive ? t('campaigns.state.active') : t('campaigns.state.stopped')}
                      </StatusChip>
                    </td>
                    <RowActions>
                      <Link className="btn btn-primary" to={`/kampagnen/${campaign.id}?tab=phone`}>
                        {t('campaigns.open')}
                      </Link>
                    </RowActions>
                  </tr>
                ))}
              </Table>
            )}
          </Section>
        </Card>
      ) : null}

      <Card>
        <Section title={t('home.customerNumber')}>
          <p className="lead">{principal.tenant.customerNumber}</p>
        </Section>
      </Card>
    </PageLayout>
  );
}
